import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { UserCog, Shield, Users, Trash2, Crown, Settings } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const modules = [
  { id: 'general', label: 'Anagrafica Generale', color: 'bg-blue-100 text-blue-700' },
  { id: 'insurance', label: 'Assicurazioni', color: 'bg-emerald-100 text-emerald-700' },
  { id: 'services', label: 'Servizi', color: 'bg-pink-100 text-pink-700' },
  { id: 'telecom', label: 'Telefonia', color: 'bg-violet-100 text-violet-700' },
  { id: 'consulting', label: 'Consulenza', color: 'bg-orange-100 text-orange-700' }
];

const roles = [
  { id: 'superadmin', label: 'Super Admin', color: 'bg-red-100 text-red-700', icon: Crown },
  { id: 'admin', label: 'Admin', color: 'bg-amber-100 text-amber-700', icon: Shield },
  { id: 'operator', label: 'Operatore', color: 'bg-slate-100 text-slate-700', icon: UserCog }
];

const UserManagement = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState('');

  const isSuperAdmin = currentUser?.role === 'superadmin';

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento utenti');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async (userId, updates) => {
    try {
      await axios.put(`${API}/users/${userId}`, updates);
      toast.success('Utente aggiornato');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Eliminare questo utente?')) return;
    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success('Utente eliminato');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleToggleActive = (user) => {
    const newStatus = !user.is_active;
    const action = newStatus ? 'abilitare' : 'disabilitare';
    if (!window.confirm(`Vuoi ${action} l'utente ${user.full_name}?`)) return;
    handleUpdateUser(user.id, { is_active: newStatus });
  };

  const handleChangeRole = (user) => {
    setEditingUser(user);
    setSelectedRole(user.role);
    setRoleDialogOpen(true);
  };

  const confirmRoleChange = () => {
    if (editingUser && selectedRole) {
      handleUpdateUser(editingUser.id, { role: selectedRole });
      setRoleDialogOpen(false);
      setEditingUser(null);
    }
  };

  const handleToggleModule = (user, moduleId) => {
    const currentModules = user.modules_access || [];
    const newModules = currentModules.includes(moduleId)
      ? currentModules.filter(m => m !== moduleId)
      : [...currentModules, moduleId];
    handleUpdateUser(user.id, { modules_access: newModules });
  };

  const openModulesDialog = (user) => {
    setEditingUser(user);
    setDialogOpen(true);
  };

  const getRoleBadge = (role) => {
    const roleConfig = roles.find(r => r.id === role) || roles[2];
    const Icon = roleConfig.icon;
    return (
      <Badge className={`${roleConfig.color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {roleConfig.label}
      </Badge>
    );
  };

  const canModifyUser = (targetUser) => {
    if (!isSuperAdmin && ['admin', 'superadmin'].includes(targetUser.role)) {
      return false;
    }
    return true;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="user-management-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Gestione Utenti</h1>
        <p className="text-slate-600 mt-1">Gestisci gli utenti e i loro permessi</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
              <Users className="w-6 h-6 text-slate-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Totale Utenti</p>
              <p className="text-2xl font-bold text-slate-900">{users.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
              <Crown className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Super Admin</p>
              <p className="text-2xl font-bold text-slate-900">{users.filter(u => u.role === 'superadmin').length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
              <Shield className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Amministratori</p>
              <p className="text-2xl font-bold text-slate-900">{users.filter(u => u.role === 'admin').length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
              <UserCog className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Operatori</p>
              <p className="text-2xl font-bold text-slate-900">{users.filter(u => u.role === 'operator').length}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Users Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Caricamento...</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Utente</TableHead>
                    <TableHead>Ruolo</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead>Moduli</TableHead>
                    <TableHead>Registrato</TableHead>
                    <TableHead className="w-24">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id} data-testid={`user-row-${user.id}`} className={!user.is_active ? 'opacity-50 bg-slate-50' : ''}>
                      <TableCell>
                        <div>
                          <p className="font-medium text-slate-900">{user.full_name}</p>
                          <p className="text-sm text-slate-500">{user.email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getRoleBadge(user.role)}
                          {isSuperAdmin && user.id !== currentUser?.id && (
                            <Button variant="ghost" size="sm" onClick={() => handleChangeRole(user)} className="text-xs h-7 px-2">
                              <Settings className="w-3 h-3 mr-1" />
                              Cambia
                            </Button>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={user.is_active !== false}
                            onCheckedChange={() => handleToggleActive(user)}
                            disabled={!canModifyUser(user) || user.id === currentUser?.id}
                            data-testid={`user-active-switch-${user.id}`}
                          />
                          <span className={`text-sm ${user.is_active !== false ? 'text-green-600' : 'text-red-600'}`}>
                            {user.is_active !== false ? 'Attivo' : 'Disabilitato'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {['admin', 'superadmin'].includes(user.role) ? (
                          <span className="text-sm text-slate-500">Tutti i moduli</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {(user.modules_access || []).map(m => {
                              const mod = modules.find(x => x.id === m);
                              return mod ? <Badge key={m} className={mod.color}>{mod.label}</Badge> : null;
                            })}
                            {(user.modules_access || []).length === 0 && (
                              <span className="text-sm text-slate-400">Nessun modulo</span>
                            )}
                            <Button variant="outline" size="sm" onClick={() => openModulesDialog(user)} className="ml-2 text-xs h-6" disabled={!canModifyUser(user)}>
                              Modifica
                            </Button>
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {new Date(user.created_at).toLocaleDateString('it-IT')}
                      </TableCell>
                      <TableCell>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => handleDeleteUser(user.id)} 
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          disabled={!canModifyUser(user) || user.id === currentUser?.id}
                          data-testid={`delete-user-${user.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modules Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifica Accesso Moduli</DialogTitle>
            <DialogDescription>
              Seleziona i moduli a cui {editingUser?.full_name} può accedere
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {modules.map((module) => (
              <div key={module.id} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center gap-3">
                  <Badge className={module.color}>{module.label}</Badge>
                </div>
                <Checkbox
                  checked={editingUser?.modules_access?.includes(module.id) || false}
                  onCheckedChange={() => {
                    if (editingUser) {
                      handleToggleModule(editingUser, module.id);
                      setEditingUser({
                        ...editingUser,
                        modules_access: editingUser.modules_access?.includes(module.id)
                          ? editingUser.modules_access.filter(m => m !== module.id)
                          : [...(editingUser.modules_access || []), module.id]
                      });
                    }
                  }}
                />
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Role Change Dialog */}
      <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cambia Ruolo Utente</DialogTitle>
            <DialogDescription>
              Modifica il ruolo di {editingUser?.full_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <Select value={selectedRole} onValueChange={setSelectedRole}>
              <SelectTrigger data-testid="role-select">
                <SelectValue placeholder="Seleziona ruolo" />
              </SelectTrigger>
              <SelectContent>
                {roles.map((role) => {
                  const Icon = role.icon;
                  return (
                    <SelectItem key={role.id} value={role.id}>
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4" />
                        {role.label}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setRoleDialogOpen(false)}>Annulla</Button>
              <Button onClick={confirmRoleChange} data-testid="confirm-role-change">Conferma</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;
